// src/lib/kinema/devtools/CommandLogPanel.tsx
import { useState, useEffect, useRef } from 'react';
import { useEditor } from '../konva/hooks';
import { WILDCARD } from '../core/types';
import type { Command, WildcardListener } from '../core/types';

interface LogEntry {
  id: number;
  command: string;
  payload: unknown;
  timestamp: Date;
  expanded: boolean;
}

export function CommandLogPanel() {
  const editor = useEditor();
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [filter, setFilter] = useState('');
  const idRef = useRef(0);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const listener: WildcardListener = (command: Command, payload: unknown) => {
      setEntries((prev) => [
        ...prev.slice(-499), // keep last 500 entries
        { id: ++idRef.current, command: command.type, payload, timestamp: new Date(), expanded: false },
      ]);
    };
    return editor.registerCommandListener(WILDCARD, listener);
  }, [editor]);

  // Auto-scroll to bottom on new entries
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries.length]);

  const toggleExpand = (id: number) => {
    setEntries((prev) => prev.map((e) => e.id === id ? { ...e, expanded: !e.expanded } : e));
  };

  const filtered = filter
    ? entries.filter((e) => e.command.toLowerCase().includes(filter.toLowerCase()))
    : entries;

  return (
    <div style={{ padding: 8, fontSize: 12, color: '#334155', background: '#ffffff', height: '100%' }}>
      <input
        placeholder="Filter commands..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{
          width: '100%', marginBottom: 8, padding: '4px 8px',
          background: '#f8fafc', color: '#334155', border: '1px solid #e2e8f0',
          borderRadius: 4, fontFamily: 'monospace', fontSize: 11, outline: 'none',
        }}
      />
      {filtered.map((entry) => (
        <div
          key={entry.id}
          onClick={() => toggleExpand(entry.id)}
          style={{ marginBottom: 2, fontFamily: 'monospace', fontSize: 11, cursor: 'pointer', padding: '2px 0', borderBottom: '1px solid #f8fafc' }}
        >
          <span style={{ color: '#94a3b8' }}>{entry.timestamp.toLocaleTimeString()}</span>{' '}
          <span style={{ color: '#16a34a', fontWeight: 500 }}>{entry.command}</span>
          {entry.expanded && (
            <pre style={{ margin: '4px 0 4px 16px', color: '#64748b', fontSize: 10, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify(entry.payload, null, 2)}
            </pre>
          )}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
